import React from 'react';
import { cn } from '../../../lib/utils';

export type MailboxLocationBadgeVariant = 'default' | 'compact';

export interface MailboxLocationBadgeProps {
  accountLabel: string;
  folderLabel: string;
  variant?: MailboxLocationBadgeVariant;
  className?: string;
}

type FolderKind = 'inbox' | 'draft' | 'sent' | 'folder';

function detectFolderKind(folder: string): FolderKind {
  const u = folder.toUpperCase();
  if (u === 'INBOX' || u.endsWith('/INBOX')) return 'inbox';
  if (u === 'DRAFT' || u.includes('DRAFT')) return 'draft';
  if (u === 'SENT' || u.includes('SENT')) return 'sent';
  return 'folder';
}

function LocationIcon({ kind, className }: { kind: FolderKind; className?: string }) {
  switch (kind) {
    case 'inbox':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 13V7a2 2 0 00-2-2H6a2 2 0 00-2 2v6m16 0v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
      );
    case 'draft':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      );
    case 'sent':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
      );
    default:
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
          />
        </svg>
      );
  }
}

/** One line: `account / folder` with a small folder-style icon (email detail headers). */
export const MailboxLocationBadge: React.FC<MailboxLocationBadgeProps> = ({
  accountLabel,
  folderLabel,
  variant = 'default',
  className,
}) => {
  const kind = detectFolderKind(folderLabel);
  const compact = variant === 'compact';
  const line = `${accountLabel} / ${folderLabel}`;

  const iconSvg = compact ? 'h-2.5 w-2.5' : 'h-3 w-3';

  return (
    <div className={cn('inline-flex max-w-full items-center gap-1 text-left', className)} title={line}>
      <span className="inline-flex shrink-0 text-slate-400 dark:text-slate-500" aria-hidden>
        <LocationIcon kind={kind} className={iconSvg} />
      </span>
      <p
        className={cn(
          'min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200',
          compact ? 'text-[10px] leading-tight' : 'text-[11px] leading-snug sm:text-xs',
        )}
      >
        <span className="font-medium">{accountLabel}</span>
        <span className="mx-0.5 font-normal text-slate-400 dark:text-slate-500">/</span>
        <span>{folderLabel}</span>
      </p>
    </div>
  );
};
